import { useRef, useEffect, useState, useCallback } from 'react';
import type { DetectionResult, BodyMeasurements, UnitSystem } from '@/types/measurements';
import { formatMeasurement } from '@/types/measurements';
import { useAppStore } from '@/lib/store';
import { exportOverlayImage } from '@/lib/overlay-export';
import { shareOverlayImage } from '@/lib/share';

type OverlayMode = 'photo' | 'silhouette';
type ViewSource = 'front' | 'side';

interface MeasurementOverlayProps {
  photo: string | null;
  detection: DetectionResult;
  measurements: BodyMeasurements;
  onClose: () => void;
  sidePhoto?: string | null;
  sideDetection?: DetectionResult | null;
  /** Ref exposed so parent can export the canvas as an image */
  canvasExportRef?: React.MutableRefObject<HTMLCanvasElement | null>;
}

// MediaPipe Pose landmark indices
const NOSE = 0;
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_ELBOW = 13;
const RIGHT_ELBOW = 14;
const LEFT_WRIST = 15;
const RIGHT_WRIST = 16;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;
const LEFT_ANKLE = 27;
const RIGHT_ANKLE = 28;

// Colors
const COLOR_LANDMARK = '#22c55e';    // brand green for direct measurements
const COLOR_CIRCUMFERENCE = '#3b82f6'; // blue for circumferences
const LABEL_BG = 'rgba(0, 0, 0, 0.7)';

export function MeasurementOverlay({
  photo,
  detection,
  measurements,
  onClose,
  sidePhoto,
  sideDetection,
  canvasExportRef,
}: MeasurementOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<OverlayMode>(photo ? 'photo' : 'silhouette');
  const [viewSource, setViewSource] = useState<ViewSource>('front');
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [sidePhotoLoaded, setSidePhotoLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const sideImgRef = useRef<HTMLImageElement | null>(null);
  const unitSystem = useAppStore((s) => s.unitSystem);
  const [sharing, setSharing] = useState(false);

  const hasSide = !!(sideDetection && sidePhoto);

  // Expose canvas ref to parent for export
  useEffect(() => {
    if (canvasExportRef) {
      canvasExportRef.current = canvasRef.current;
    }
  });

  // Active detection/photo based on view source
  const activeDetection = viewSource === 'side' && sideDetection ? sideDetection : detection;
  const activePhoto = viewSource === 'side' && sidePhoto ? sidePhoto : photo;
  const activeImg = viewSource === 'side' ? sideImgRef.current : imgRef.current;
  const activePhotoLoaded = viewSource === 'side' ? sidePhotoLoaded : photoLoaded;

  // Load front photo image
  useEffect(() => {
    if (!photo) {
      setPhotoLoaded(false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setPhotoLoaded(true);
    };
    img.src = photo;
  }, [photo]);

  // Load side photo image
  useEffect(() => {
    if (!sidePhoto) {
      setSidePhotoLoaded(false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      sideImgRef.current = img;
      setSidePhotoLoaded(true);
    };
    img.src = sidePhoto;
  }, [sidePhoto]);

  /** Map normalized landmark coords to canvas pixel coords (object-fit: contain). */
  const toCanvas = useCallback(
    (normX: number, normY: number, cw: number, ch: number): [number, number] => {
      const { imageWidth: vw, imageHeight: vh } = activeDetection;
      if (vw === 0 || vh === 0) return [0, 0];

      const videoAspect = vw / vh;
      const canvasAspect = cw / ch;

      let scale: number, offsetX: number, offsetY: number;

      if (videoAspect > canvasAspect) {
        // Image wider — letterbox top/bottom
        scale = cw / vw;
        offsetX = 0;
        offsetY = (ch - vh * scale) / 2;
      } else {
        // Image taller — pillarbox left/right
        scale = ch / vh;
        offsetX = (cw - vw * scale) / 2;
        offsetY = 0;
      }

      const px = normX * vw * scale + offsetX;
      const py = normY * vh * scale + offsetY;
      return [px, py];
    },
    [activeDetection]
  );

  // Draw overlay
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const cw = rect.width;
    const ch = rect.height;
    ctx.clearRect(0, 0, cw, ch);

    if (mode === 'photo' && activePhotoLoaded && activeImg) {
      drawPhotoBackground(ctx, activeImg, cw, ch);
    } else {
      drawSilhouetteBackground(ctx, cw, ch, activeDetection, toCanvas);
    }

    drawMeasurementLines(ctx, cw, ch, activeDetection, measurements, toCanvas, mode, unitSystem, viewSource);
  }, [mode, activePhotoLoaded, activeImg, activeDetection, measurements, toCanvas, unitSystem, viewSource]);

  async function handleShareImage() {
    const canvas = canvasRef.current;
    if (!canvas || sharing) return;
    setSharing(true);
    try {
      const blob = await exportOverlayImage(canvas);
      await shareOverlayImage(blob);
    } catch {
      // Silently fail
    } finally {
      setSharing(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="text-sm font-semibold text-white">Measurement Overlay</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShareImage}
            disabled={sharing}
            className="flex h-8 items-center gap-1 rounded-full bg-brand-500/80 px-3 text-xs font-medium text-white disabled:opacity-50"
            aria-label="Share image"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {sharing ? 'Sharing...' : 'Share'}
          </button>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white"
            aria-label="Close overlay"
          >
            &#x2715;
          </button>
        </div>
      </div>

      {/* Toggles row */}
      <div className="flex items-center justify-center gap-2 px-4 pb-3">
        {/* Front/Side toggle (only when side data exists) */}
        {hasSide && (
          <div className="flex rounded-lg bg-white/10 p-1">
            <button
              onClick={() => setViewSource('front')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                viewSource === 'front'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white/60'
              }`}
            >
              Front
            </button>
            <button
              onClick={() => setViewSource('side')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                viewSource === 'side'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white/60'
              }`}
            >
              Side
            </button>
          </div>
        )}

        {/* Mode toggle */}
        <div className="flex rounded-lg bg-white/10 p-1">
          <button
            onClick={() => setMode('photo')}
            disabled={!activePhoto}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              mode === 'photo'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-white/60'
            } ${!activePhoto ? 'cursor-not-allowed opacity-30' : ''}`}
          >
            On Photo
          </button>
          <button
            onClick={() => setMode('silhouette')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              mode === 'silhouette'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-white/60'
            }`}
          >
            Body Map
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0"
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 px-4 py-3">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-4 rounded-full" style={{ background: COLOR_LANDMARK }} />
          <span className="text-xs text-white/60">Direct</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-4 rounded-full" style={{ background: COLOR_CIRCUMFERENCE }} />
          <span className="text-xs text-white/60">Circumference</span>
        </div>
      </div>
    </div>
  );
}

// ─── Drawing helpers ────────────────────────────────────────────

function drawPhotoBackground(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cw: number,
  ch: number
) {
  const imgAspect = img.width / img.height;
  const canvasAspect = cw / ch;

  let dw: number, dh: number, dx: number, dy: number;

  if (imgAspect > canvasAspect) {
    dw = cw;
    dh = cw / imgAspect;
    dx = 0;
    dy = (ch - dh) / 2;
  } else {
    dh = ch;
    dw = ch * imgAspect;
    dx = (cw - dw) / 2;
    dy = 0;
  }

  // Dim background
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, cw, ch);
  ctx.drawImage(img, dx, dy, dw, dh);

  // Slight darkening for contrast with measurement lines
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.fillRect(dx, dy, dw, dh);
}

function drawSilhouetteBackground(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  detection: DetectionResult,
  toCanvas: (nx: number, ny: number, cw: number, ch: number) => [number, number]
) {
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(0, 0, cw, ch);

  const { landmarks } = detection;

  // Draw body outline using landmarks
  ctx.save();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';

  const connections: [number, number][] = [
    [11, 12], // shoulders
    [11, 23], [12, 24], // torso sides
    [23, 24], // hips
    [11, 13], [13, 15], // left arm
    [12, 14], [14, 16], // right arm
    [23, 25], [25, 27], // left leg
    [24, 26], [26, 28], // right leg
  ];

  for (const [from, to] of connections) {
    const lmFrom = landmarks[from];
    const lmTo = landmarks[to];
    if (!lmFrom || !lmTo) continue;

    const [x1, y1] = toCanvas(lmFrom.x, lmFrom.y, cw, ch);
    const [x2, y2] = toCanvas(lmTo.x, lmTo.y, cw, ch);

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  // Draw head circle
  const nose = landmarks[NOSE];
  if (nose) {
    const [nx, ny] = toCanvas(nose.x, nose.y, cw, ch);
    const lShoulder = landmarks[LEFT_SHOULDER];
    const rShoulder = landmarks[RIGHT_SHOULDER];
    if (lShoulder && rShoulder) {
      const [lsx] = toCanvas(lShoulder.x, lShoulder.y, cw, ch);
      const [rsx] = toCanvas(rShoulder.x, rShoulder.y, cw, ch);
      const shoulderSpan = Math.abs(rsx - lsx);
      const headRadius = shoulderSpan * 0.25;
      ctx.beginPath();
      ctx.arc(nx, ny - headRadius * 0.5, headRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  // Draw landmark dots
  const keyIndices = [NOSE, LEFT_SHOULDER, RIGHT_SHOULDER, LEFT_ELBOW, RIGHT_ELBOW,
    LEFT_WRIST, RIGHT_WRIST, LEFT_HIP, RIGHT_HIP, LEFT_ANKLE, RIGHT_ANKLE];
  for (const idx of keyIndices) {
    const lm = landmarks[idx];
    if (!lm || lm.visibility < 0.3) continue;
    const [x, y] = toCanvas(lm.x, lm.y, cw, ch);
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fill();
  }

  ctx.restore();
}

// ─── Leader-line label system ──────────────────────────────────

interface LabelEntry {
  text: string;
  color: string;
  bodyX: number;  // attachment point on body (canvas px)
  bodyY: number;
  side: 'left' | 'right';
}

const MIN_LABEL_GAP = 28;

/** Multi-pass constraint solver: resolve vertical overlaps without exceeding canvas bounds */
function resolveOverlaps(entries: LabelEntry[], canvasHeight: number): number[] {
  if (entries.length === 0) return [];
  const ys = entries.map((e) => e.bodyY);

  const minY = 30;
  const maxY = canvasHeight - 30;

  // Pass 1: Push-down — ensure minimum gap between consecutive labels
  for (let i = 1; i < ys.length; i++) {
    if (ys[i]! - ys[i - 1]! < MIN_LABEL_GAP) {
      ys[i] = ys[i - 1]! + MIN_LABEL_GAP;
    }
  }

  // Pass 2: Pull-up if bottom label exceeds canvas
  if (ys.length > 0 && ys[ys.length - 1]! > maxY) {
    const excess = ys[ys.length - 1]! - maxY;
    for (let i = 0; i < ys.length; i++) {
      ys[i]! -= excess;
    }
  }

  // Pass 3: Ensure minimum top margin
  if (ys.length > 0 && ys[0]! < minY) {
    const shift = minY - ys[0]!;
    for (let i = 0; i < ys.length; i++) {
      ys[i]! += shift;
    }
  }

  // Pass 4: If labels can't fit with MIN_LABEL_GAP, compress gaps proportionally
  const totalNeeded = (ys.length - 1) * MIN_LABEL_GAP;
  const available = maxY - minY;
  if (totalNeeded > available && ys.length > 1) {
    const reducedGap = available / (ys.length - 1);
    for (let i = 0; i < ys.length; i++) {
      ys[i] = minY + i * reducedGap;
    }
  }

  return ys;
}

/** Draw all collected labels in margin gutters with leader lines to body points */
function drawLeaderLabels(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  labels: LabelEntry[]
) {
  const leftLabels = labels.filter((l) => l.side === 'left').sort((a, b) => a.bodyY - b.bodyY);
  const rightLabels = labels.filter((l) => l.side === 'right').sort((a, b) => a.bodyY - b.bodyY);

  const leftYs = resolveOverlaps(leftLabels, ch);
  const rightYs = resolveOverlaps(rightLabels, ch);

  const MARGIN = 8;
  /** Horizontal stagger per label index to prevent leader lines crossing */
  const ELBOW_STAGGER = 6;

  // Draw left labels
  for (let i = 0; i < leftLabels.length; i++) {
    const entry = leftLabels[i]!;
    const labelY = leftYs[i]!;
    const labelX = MARGIN;

    // Measure label width
    ctx.save();
    ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif';
    const metrics = ctx.measureText(entry.text);
    const pillW = metrics.width + 16;
    const pillCenterX = labelX + pillW / 2;
    ctx.restore();

    const elbowOffset = 8 + i * ELBOW_STAGGER;
    drawLeaderLine(ctx, labelX + pillW, labelY, entry.bodyX, entry.bodyY, entry.color, elbowOffset);
    drawLabel(ctx, pillCenterX, labelY, entry.text, entry.color);
  }

  // Draw right labels
  for (let i = 0; i < rightLabels.length; i++) {
    const entry = rightLabels[i]!;
    const labelY = rightYs[i]!;

    ctx.save();
    ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif';
    const metrics = ctx.measureText(entry.text);
    const pillW = metrics.width + 16;
    ctx.restore();

    const labelX = cw - MARGIN - pillW;
    const pillCenterX = labelX + pillW / 2;

    const elbowOffset = -(8 + i * ELBOW_STAGGER);
    drawLeaderLine(ctx, labelX, labelY, entry.bodyX, entry.bodyY, entry.color, elbowOffset);
    drawLabel(ctx, pillCenterX, labelY, entry.text, entry.color);
  }
}

/**
 * Draw an elbow-routed leader line from label edge to body attachment point.
 * @param elbowOffset Horizontal offset from label edge for the elbow joint.
 *   Positive = right of fromX, negative = left of fromX.
 *   Staggering this per label prevents lines from crossing.
 */
function drawLeaderLine(
  ctx: CanvasRenderingContext2D,
  fromX: number, fromY: number,
  toX: number, toY: number,
  color: string,
  elbowOffset = 0
) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  // Staggered elbow joint to prevent crossing lines
  const elbowX = fromX + elbowOffset;
  ctx.lineTo(elbowX, fromY);
  ctx.lineTo(elbowX, toY);
  ctx.lineTo(toX, toY);
  ctx.stroke();
  // Dot at body attachment point
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.arc(toX, toY, 3, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

function drawMeasurementLines(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  detection: DetectionResult,
  measurements: BodyMeasurements,
  toCanvas: (nx: number, ny: number, cw: number, ch: number) => [number, number],
  _mode: OverlayMode,
  unit: UnitSystem = 'cm',
  viewSource: ViewSource = 'front'
) {
  const { landmarks } = detection;
  const labels: LabelEntry[] = [];

  // For side view with limited landmarks, show what we can
  const lShoulder = landmarks[LEFT_SHOULDER];
  const rShoulder = landmarks[RIGHT_SHOULDER];
  const lHip = landmarks[LEFT_HIP];
  const rHip = landmarks[RIGHT_HIP];

  // Need at least some torso landmarks
  if (!lShoulder && !rShoulder) {
    // Draw "No landmarks detected" text
    ctx.save();
    ctx.font = 'bold 14px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.textAlign = 'center';
    ctx.fillText('Limited landmarks in this view', cw / 2, ch / 2);
    ctx.restore();
    return;
  }

  // Use available shoulder/hip or fallback
  const sL = lShoulder ?? rShoulder!;
  const sR = rShoulder ?? lShoulder!;
  const hL = lHip ?? rHip ?? sL;
  const hR = rHip ?? lHip ?? sR;

  const shoulderMidY = (sL.y + sR.y) / 2;
  const hipMidY = (hL.y + hR.y) / 2;
  const torsoSpan = hipMidY - shoulderMidY;

  if (viewSource === 'side') {
    // ── Side view: show circumferences as depth indicators + height ──
    const midX = (sL.x + sR.x) / 2;

    // Height (if visible)
    const nose = landmarks[NOSE];
    const anyAnkle = landmarks[LEFT_ANKLE] ?? landmarks[RIGHT_ANKLE];
    if (measurements.height?.valueCm > 0 && nose && anyAnkle) {
      const headTopY = nose.y - (shoulderMidY - nose.y) * 0.8;
      const xPos = midX - 0.08;
      const [x1, y1] = toCanvas(xPos, headTopY, cw, ch);
      const [x2, y2] = toCanvas(xPos, anyAnkle.y + 0.02, cw, ch);
      drawLine(ctx, x1, y1, x2, y2, COLOR_LANDMARK);
      labels.push({
        text: `Height: ${formatMeasurement(measurements.height.valueCm, unit)}`,
        color: COLOR_LANDMARK, bodyX: x1, bodyY: (y1 + y2) / 2, side: 'left',
      });
    }

    // Torso depth indicators at chest/waist/hip
    const chestY = shoulderMidY + torsoSpan * 0.3;
    const waistY = shoulderMidY + torsoSpan * 0.6;

    if (measurements.chestCircumference?.valueCm > 0) {
      const [bodyX, bodyY] = drawDepthLine(ctx, cw, ch, detection, toCanvas, chestY, COLOR_CIRCUMFERENCE);
      labels.push({
        text: `Chest: ${formatMeasurement(measurements.chestCircumference.valueCm, unit)}`,
        color: COLOR_CIRCUMFERENCE, bodyX, bodyY, side: 'right',
      });
    }
    if (measurements.waistCircumference?.valueCm > 0) {
      const [bodyX, bodyY] = drawDepthLine(ctx, cw, ch, detection, toCanvas, waistY, COLOR_CIRCUMFERENCE);
      labels.push({
        text: `Waist: ${formatMeasurement(measurements.waistCircumference.valueCm, unit)}`,
        color: COLOR_CIRCUMFERENCE, bodyX, bodyY, side: 'right',
      });
    }
    if (measurements.hipCircumference?.valueCm > 0) {
      const [bodyX, bodyY] = drawDepthLine(ctx, cw, ch, detection, toCanvas, hipMidY, COLOR_CIRCUMFERENCE);
      labels.push({
        text: `Hip: ${formatMeasurement(measurements.hipCircumference.valueCm, unit)}`,
        color: COLOR_CIRCUMFERENCE, bodyX, bodyY, side: 'right',
      });
    }
  } else {
    // ── Front view: full measurement set ──

    // 1. Shoulder width
    if (measurements.shoulderWidth?.valueCm > 0 && lShoulder && rShoulder) {
      const [x1, y1] = toCanvas(lShoulder.x, lShoulder.y, cw, ch);
      const [x2, y2] = toCanvas(rShoulder.x, rShoulder.y, cw, ch);
      drawLine(ctx, x1, y1, x2, y2, COLOR_LANDMARK);
      labels.push({
        text: formatMeasurement(measurements.shoulderWidth.valueCm, unit),
        color: COLOR_LANDMARK,
        bodyX: (x1 + x2) / 2,
        bodyY: (y1 + y2) / 2,
        side: 'left',
      });
    }

    // 2. Arm length (better-visible side)
    if (measurements.armLength?.valueCm > 0) {
      const lS = landmarks[LEFT_SHOULDER];
      const rS = landmarks[RIGHT_SHOULDER];
      const lE = landmarks[LEFT_ELBOW];
      const rE = landmarks[RIGHT_ELBOW];
      const lW = landmarks[LEFT_WRIST];
      const rW = landmarks[RIGHT_WRIST];

      const leftVis = (lS && lE && lW) ? Math.min(lS.visibility, lE.visibility, lW.visibility) : 0;
      const rightVis = (rS && rE && rW) ? Math.min(rS.visibility, rE.visibility, rW.visibility) : 0;

      if (leftVis > 0 || rightVis > 0) {
        const useLeft = leftVis >= rightVis;
        const shoulder = useLeft ? lS! : rS!;
        const elbow = useLeft ? lE! : rE!;
        const wrist = useLeft ? lW! : rW!;

        const [sx, sy] = toCanvas(shoulder.x, shoulder.y, cw, ch);
        const [ex, ey] = toCanvas(elbow.x, elbow.y, cw, ch);
        const [wx, wy] = toCanvas(wrist.x, wrist.y, cw, ch);

        drawPolyline(ctx, [[sx, sy], [ex, ey], [wx, wy]], COLOR_LANDMARK);
        labels.push({
          text: `Arm: ${formatMeasurement(measurements.armLength.valueCm, unit)}`,
          color: COLOR_LANDMARK,
          bodyX: ex,
          bodyY: ey,
          side: 'left',
        });
      }
    }

    // 3. Back length
    if (measurements.backLength?.valueCm > 0) {
      const neckMidX = (sL.x + sR.x) / 2;
      const neckMidY = shoulderMidY;
      const hipMidX = (hL.x + hR.x) / 2;

      const [x1, y1] = toCanvas(neckMidX + 0.04, neckMidY, cw, ch);
      const [x2, y2] = toCanvas(hipMidX + 0.04, hipMidY, cw, ch);
      drawLine(ctx, x1, y1, x2, y2, COLOR_LANDMARK);
      labels.push({
        text: `Back: ${formatMeasurement(measurements.backLength.valueCm, unit)}`,
        color: COLOR_LANDMARK,
        bodyX: (x1 + x2) / 2,
        bodyY: (y1 + y2) / 2,
        side: 'left',
      });
    }

    // 4. Height
    if (measurements.height?.valueCm > 0) {
      const nose = landmarks[NOSE];
      const lAnkle = landmarks[LEFT_ANKLE];
      const rAnkle = landmarks[RIGHT_ANKLE];
      if (nose && (lAnkle || rAnkle)) {
        const ankleY = Math.max(lAnkle?.y ?? 0, rAnkle?.y ?? 0);
        const headTopY = nose.y - (shoulderMidY - nose.y) * 0.8;
        const xPos = Math.min(sL.x, sR.x) - 0.06;
        const [x1, y1] = toCanvas(xPos, headTopY, cw, ch);
        const [x2, y2] = toCanvas(xPos, ankleY + 0.02, cw, ch);
        drawLine(ctx, x1, y1, x2, y2, COLOR_LANDMARK);
        labels.push({
          text: `Height: ${formatMeasurement(measurements.height.valueCm, unit)}`,
          color: COLOR_LANDMARK,
          bodyX: x1,
          bodyY: (y1 + y2) / 2,
          side: 'left',
        });
      }
    }

    // 5. Chest circumference
    if (measurements.chestCircumference?.valueCm > 0) {
      const chestY = shoulderMidY + torsoSpan * 0.3;
      const [bodyX, bodyY] = drawCircumferenceLine(ctx, cw, ch, detection, toCanvas, chestY, COLOR_CIRCUMFERENCE);
      labels.push({
        text: `Chest: ${formatMeasurement(measurements.chestCircumference.valueCm, unit)}`,
        color: COLOR_CIRCUMFERENCE,
        bodyX,
        bodyY,
        side: 'right',
      });
    }

    // 6. Waist circumference
    if (measurements.waistCircumference?.valueCm > 0) {
      const waistY = shoulderMidY + torsoSpan * 0.6;
      const [bodyX, bodyY] = drawCircumferenceLine(ctx, cw, ch, detection, toCanvas, waistY, COLOR_CIRCUMFERENCE);
      labels.push({
        text: `Waist: ${formatMeasurement(measurements.waistCircumference.valueCm, unit)}`,
        color: COLOR_CIRCUMFERENCE,
        bodyX,
        bodyY,
        side: 'right',
      });
    }

    // 7. Hip circumference
    if (measurements.hipCircumference?.valueCm > 0) {
      const [bodyX, bodyY] = drawCircumferenceLine(ctx, cw, ch, detection, toCanvas, hipMidY, COLOR_CIRCUMFERENCE);
      labels.push({
        text: `Hip: ${formatMeasurement(measurements.hipCircumference.valueCm, unit)}`,
        color: COLOR_CIRCUMFERENCE,
        bodyX,
        bodyY,
        side: 'right',
      });
    }
  }

  // Draw all labels with leader lines (no overlaps)
  drawLeaderLabels(ctx, cw, ch, labels);
}

/** Draw a simple measurement line with end-caps (no label). */
function drawLine(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number,
  x2: number, y2: number,
  color: string
) {
  ctx.save();

  // White outline for contrast
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Colored line
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // End-caps (perpendicular)
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len > 0) {
    const capLen = 6;
    const nx = -dy / len;
    const ny = dx / len;

    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x1 + nx * capLen, y1 + ny * capLen);
    ctx.lineTo(x1 - nx * capLen, y1 - ny * capLen);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x2 + nx * capLen, y2 + ny * capLen);
    ctx.lineTo(x2 - nx * capLen, y2 - ny * capLen);
    ctx.stroke();
  }

  ctx.restore();
}

/** Draw a polyline (multi-segment, no label). */
function drawPolyline(
  ctx: CanvasRenderingContext2D,
  points: [number, number][],
  color: string
) {
  if (points.length < 2) return;
  ctx.save();

  // White outline
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = 4;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(points[0]![0], points[0]![1]);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i]![0], points[i]![1]);
  }
  ctx.stroke();

  // Colored line
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(points[0]![0], points[0]![1]);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i]![0], points[i]![1]);
  }
  ctx.stroke();

  // End-cap dots
  for (const [x, y] of points) {
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }

  ctx.restore();
}

/** Draw a horizontal circumference indicator (no label). Returns midpoint for leader line. */
function drawCircumferenceLine(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  detection: DetectionResult,
  toCanvas: (nx: number, ny: number, cw: number, ch: number) => [number, number],
  normY: number,
  color: string
): [number, number] {
  const { landmarks } = detection;
  const ls = landmarks[LEFT_SHOULDER] ?? landmarks[RIGHT_SHOULDER];
  const rs = landmarks[RIGHT_SHOULDER] ?? landmarks[LEFT_SHOULDER];
  const lh = landmarks[LEFT_HIP] ?? landmarks[RIGHT_HIP] ?? ls;
  const rh = landmarks[RIGHT_HIP] ?? landmarks[LEFT_HIP] ?? rs;

  if (!ls || !rs) return [cw / 2, normY * ch];

  const shoulderMidY = (ls.y + rs.y) / 2;
  const hipMidY = (lh!.y + rh!.y) / 2;

  const t = hipMidY > shoulderMidY
    ? Math.max(0, Math.min(1, (normY - shoulderMidY) / (hipMidY - shoulderMidY)))
    : 0.5;

  const leftX = ls.x + (lh!.x - ls.x) * t;
  const rightX = rs.x + (rh!.x - rs.x) * t;

  const expansion = 0.03;
  const [x1, y1] = toCanvas(leftX - expansion, normY, cw, ch);
  const [x2, y2] = toCanvas(rightX + expansion, normY, cw, ch);

  ctx.save();

  // White outline
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 4;
  ctx.setLineDash([8, 4]);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  // Colored dashed line
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  ctx.setLineDash([]);

  // Small double-arrow indicators at ends
  const arrowLen = 6;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x1 + arrowLen, y1 - arrowLen);
  ctx.lineTo(x1, y1);
  ctx.lineTo(x1 + arrowLen, y1 + arrowLen);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x2 - arrowLen, y2 - arrowLen);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2 - arrowLen, y2 + arrowLen);
  ctx.stroke();

  ctx.restore();

  // Return the right-side midpoint as leader line attachment
  return [x2, (y1 + y2) / 2];
}

/** Draw a depth indicator for side view (horizontal dashed line across body silhouette). */
function drawDepthLine(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  detection: DetectionResult,
  toCanvas: (nx: number, ny: number, cw: number, ch: number) => [number, number],
  normY: number,
  color: string
): [number, number] {
  // For side view, use segmentation mask extents if available,
  // otherwise approximate with a fixed body width at the given Y level
  const { landmarks } = detection;
  const anyShoulder = landmarks[LEFT_SHOULDER] ?? landmarks[RIGHT_SHOULDER];
  if (!anyShoulder) return [cw * 0.7, normY * ch];

  // Approximate body depth as ~30% of canvas width centered on shoulder X
  const centerX = anyShoulder.x;
  const depthHalf = 0.08; // normalized half-depth
  const [x1, y1] = toCanvas(centerX - depthHalf, normY, cw, ch);
  const [x2, y2] = toCanvas(centerX + depthHalf, normY, cw, ch);

  ctx.save();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 4;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.setLineDash([]);

  // Arrows
  const arrowLen = 5;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x1 + arrowLen, y1 - arrowLen);
  ctx.lineTo(x1, y1);
  ctx.lineTo(x1 + arrowLen, y1 + arrowLen);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2 - arrowLen, y2 - arrowLen);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2 - arrowLen, y2 + arrowLen);
  ctx.stroke();

  ctx.restore();
  return [x2, (y1 + y2) / 2];
}

/** Draw a text label with a pill-shaped background. */
function drawLabel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  text: string,
  _accentColor: string
) {
  ctx.save();
  ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const metrics = ctx.measureText(text);
  const paddingX = 8;
  const paddingY = 5;
  const w = metrics.width + paddingX * 2;
  const h = 18 + paddingY;
  const rx = x - w / 2;
  const ry = y - h / 2;

  // Pill background
  ctx.fillStyle = LABEL_BG;
  ctx.beginPath();
  ctx.roundRect(rx, ry, w, h, 9);
  ctx.fill();

  // Text
  ctx.fillStyle = '#fff';
  ctx.fillText(text, x, y);

  ctx.restore();
}
